// Global Instructions Rule Applied! - March 31, 2026
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Checkbox, Collapse, Divider, Input, Tabs } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { formatKeyToLabel } from '../../utils/utilities.ts'
import type { ViewConfigColumn } from './types.ts'

const RELATED_MODULE_KEYS = new Set(['accounts', 'rounds'])

function isRelatedColumnLegacy(col: ViewConfigColumn): boolean {
  return Boolean(col.moduleKey && RELATED_MODULE_KEYS.has(col.moduleKey))
}

function moduleSectionLabel(moduleKey: string): string {
  return formatKeyToLabel(moduleKey, false)
}

function normalizeColumnTitle(title: string): string {
  return title.trim().toLowerCase()
}

function rankColumnVariant(col: ViewConfigColumn, checkedKeys: Set<string>): number {
  // Keep user-selected column variant stable when already chosen.
  if (checkedKeys.has(col.valueKey)) return 100

  let score = 0

  // Prefer display-oriented variants when duplicate titles exist (label over code).
  if (col.labelKey) score += 20
  if (col.valueKey.endsWith('__label') || col.valueKey.endsWith('_label')) score += 12
  if (col.valueKey.endsWith('__code') || col.valueKey.endsWith('_code')) score -= 8

  // City/Country columns are usually best represented by label-backed value keys.
  if ((col.fieldType === 'city' || col.fieldType === 'country') && col.labelKey) {
    score += 8
  }

  return score
}

/**
 * Config often defines multiple `field_name` rows with the same `column_title` (e.g. code vs label).
 * Show one row per display title; if several variants exist, prefer the one already on the grid.
 */
function dedupeColumnsByDisplayTitle(columns: ViewConfigColumn[], checkedKeys: Set<string>): ViewConfigColumn[] {
  const buckets = new Map<string, ViewConfigColumn[]>()
  for (const col of columns) {
    const k = normalizeColumnTitle(col.columnTitle)
    if (!buckets.has(k)) buckets.set(k, [])
    buckets.get(k)!.push(col)
  }
  const chosen: ViewConfigColumn[] = []
  for (const group of buckets.values()) {
    if (group.length === 1) {
      chosen.push(group[0])
      continue
    }
    const sorted = [...group].sort((a, b) => {
      const rankDiff = rankColumnVariant(b, checkedKeys) - rankColumnVariant(a, checkedKeys)
      if (rankDiff !== 0) return rankDiff
      return a.valueKey.localeCompare(b.valueKey)
    })
    chosen.push(sorted[0])
  }
  chosen.sort(
    (a, b) =>
      a.columnTitle.localeCompare(b.columnTitle, undefined, { sensitivity: 'base' }) ||
      a.valueKey.localeCompare(b.valueKey)
  )
  return chosen
}

function filterColumnsBySearch(columns: ViewConfigColumn[], search: string): ViewConfigColumn[] {
  const q = search.trim().toLowerCase()
  if (!q) return columns
  return columns.filter((c) => {
    const title = c.columnTitle.toLowerCase()
    const key = c.valueKey.toLowerCase()
    return title.includes(q) || key.includes(q)
  })
}

export interface AddColumnsCatalog {
  deal: ViewConfigColumn[]
  related: ViewConfigColumn[]
}

export interface AddColumnsPanelProps {
  /** Columns currently on the grid — checked keys and order reference */
  orderedColumns: ViewConfigColumn[]
  /**
   * When set (e.g. Deals), Deal / Related tabs list all catalog columns for modules linked
   * via `reporting_modules`. When omitted, legacy: `allColumns` + hard-coded module split.
   */
  addColumnsCatalog?: AddColumnsCatalog
  /** @deprecated Use `addColumnsCatalog` — full list for legacy Add columns behavior */
  allColumns?: ViewConfigColumn[]
  /** Controlled selection (Edit columns flow). When both set, internal state is not used. */
  checkedKeys?: Set<string>
  onCheckedKeysChange?: (next: Set<string>) => void
  /** First / second tab labels (default Deal / Related). */
  tabLabels?: { primary: string; related: string }
}

export function AddColumnsPanel({
  orderedColumns,
  addColumnsCatalog,
  allColumns = [],
  checkedKeys: controlledChecked,
  onCheckedKeysChange,
  tabLabels
}: AddColumnsPanelProps) {
  const primaryLabel = tabLabels?.primary ?? 'Deal'
  const relatedLabel = tabLabels?.related ?? 'Related'
  const [activeTab, setActiveTab] = useState('deal')
  const [search, setSearch] = useState('')

  const [internalChecked, setInternalChecked] = useState<Set<string>>(
    () => new Set(orderedColumns.map((c) => c.valueKey))
  )

  const isControlled = controlledChecked !== undefined && onCheckedKeysChange !== undefined
  const checkedKeys = isControlled ? controlledChecked! : internalChecked
  // Controlled callers get the resolved Set (an updater is applied to the
  // controlled value); uncontrolled state keeps React's functional update.
  const setCheckedKeys = useCallback(
    (update: React.SetStateAction<Set<string>>) => {
      if (isControlled) onCheckedKeysChange!(typeof update === 'function' ? update(controlledChecked!) : update)
      else setInternalChecked(update)
    },
    [isControlled, onCheckedKeysChange, controlledChecked]
  )

  useEffect(() => {
    if (isControlled) return
    setInternalChecked(new Set(orderedColumns.map((c) => c.valueKey)))
  }, [orderedColumns, isControlled])

  const dealSource = addColumnsCatalog?.deal ?? allColumns.filter((c) => !isRelatedColumnLegacy(c))
  const relatedSource = addColumnsCatalog?.related ?? allColumns.filter(isRelatedColumnLegacy)

  const searchFilteredDeal = useMemo(
    () => filterColumnsBySearch(dealSource, search),
    [dealSource, search]
  )

  const searchFilteredRelated = useMemo(
    () => filterColumnsBySearch(relatedSource, search),
    [relatedSource, search]
  )

  const dealTabColumns = useMemo(
    () => dedupeColumnsByDisplayTitle(searchFilteredDeal, checkedKeys),
    [searchFilteredDeal, checkedKeys]
  )

  const hasRelatedColumns = useMemo(() => {
    const dedupedRelated = dedupeColumnsByDisplayTitle(relatedSource, checkedKeys)
    return dedupedRelated.length > 0
  }, [relatedSource, checkedKeys])

  useEffect(() => {
    if (activeTab === 'related' && !hasRelatedColumns) {
      setActiveTab('deal')
    }
  }, [activeTab, hasRelatedColumns])

  const relatedGroups = useMemo(() => {
    const map = new Map<string, ViewConfigColumn[]>()
    for (const col of searchFilteredRelated) {
      const mk = col.moduleKey ?? 'other'
      if (!map.has(mk)) map.set(mk, [])
      map.get(mk)!.push(col)
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([moduleKey, columns]) => ({
        moduleKey,
        columns: dedupeColumnsByDisplayTitle(columns, checkedKeys)
      }))
  }, [searchFilteredRelated, checkedKeys])

  const toggleKey = useCallback(
    (valueKey: string, checked: boolean) => {
      setCheckedKeys((prev) => {
        const next = new Set(prev)
        if (checked) next.add(valueKey)
        else next.delete(valueKey)
        return next
      })
    },
    [setCheckedKeys]
  )

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col gap-0'>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size='small'
        className='add-columns-tabs mb-2 shrink-0 [&_.ant-tabs-content-holder]:hidden [&_.ant-tabs-nav]:mb-2'
        items={
          hasRelatedColumns
            ? [
                { key: 'deal', label: primaryLabel },
                { key: 'related', label: relatedLabel }
              ]
            : [{ key: 'deal', label: primaryLabel }]
        }
      />

      <div className='mb-2 shrink-0 overflow-hidden rounded-md border border-gray-200'>
        <Input
          variant='borderless'
          placeholder='Search'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className='w-full px-2 py-1.5'
          prefix={<FontAwesomeIcon icon={faSearch} className='text-gray-400' />}
        />
      </div>

      <Divider className='!my-0 shrink-0' />

      {activeTab === 'deal' ? (
        <ul className='mt-2 flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto pr-1'>
          {dealTabColumns.map((col) => (
            <li
              key={col.valueKey}
              className='flex items-start gap-3 border-b border-gray-100 py-2.5 last:border-b-0'
            >
              <Checkbox
                checked={checkedKeys.has(col.valueKey)}
                onChange={(e) => toggleKey(col.valueKey, e.target.checked)}
                className='pt-0.5'
              />
              <span className='min-w-0 flex-1 text-sm leading-snug text-gray-900'>{col.columnTitle}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className='mt-2 flex min-h-0 flex-1 flex-col overflow-y-auto pr-1'>
          <Collapse
            bordered={false}
            size='small'
            defaultActiveKey={relatedGroups.map((g) => g.moduleKey)}
            className='!bg-transparent [&_.ant-collapse-item]:!border-b [&_.ant-collapse-item]:border-gray-100 [&_.ant-collapse-header]:!bg-gray-200 [&_.ant-collapse-header]:!py-2 [&_.ant-collapse-content-box]:!pt-0 [&_.ant-collapse-content-box]:!pb-2'
            items={relatedGroups.map(({ moduleKey, columns }) => ({
              key: moduleKey,
              label: <span className='text-sm font-medium text-gray-900'>{moduleSectionLabel(moduleKey)}</span>,
              children: (
                <ul className='flex flex-col gap-0'>
                  {columns.map((col) => (
                    <li
                      key={col.valueKey}
                      className='flex items-start gap-3 border-b border-gray-100 py-2 last:border-b-0'
                    >
                      <Checkbox
                        checked={checkedKeys.has(col.valueKey)}
                        onChange={(e) => toggleKey(col.valueKey, e.target.checked)}
                        className='pt-0.5'
                      />
                      <span className='min-w-0 flex-1 text-sm leading-snug text-gray-900'>{col.columnTitle}</span>
                    </li>
                  ))}
                </ul>
              )
            }))}
          />
        </div>
      )}
    </div>
  )
}
